import {$computeTableMapSkipCellCheck, TableCellNode, TableNode} from '@lexical/table';
import {$isElementNode, $isTextNode} from 'lexical';

export const DEFAULT_COLUMN_WIDTH = 100;
export const BUTTON_SIZE = 16;

/**
 * Checks if all cells in the selection are empty
 */
export function areAllCellsEmpty(cells: TableCellNode[]): boolean {
  return cells.every((cell) => {
    const children = cell.getChildren();
    if (children.length === 0) return true;

    // Check each child node
    return children.every((child) => {
      // If it's a text node, check if it's empty
      if ($isTextNode(child)) {
        return child.getTextContent().trim() === '';
      }
      // If it's an element node (like paragraph), check its children
      if ($isElementNode(child)) {
        const grandChildren = child.getChildren();
        if (grandChildren.length === 0) return true;

        return grandChildren.every((grandChild) => {
          if ($isTextNode(grandChild)) {
            return grandChild.getTextContent().trim() === '';
          }
          return false;
        });
      }
      return false;
    });
  });
}

/**
 * Determines if the selection is a full row or column selection
 */
export function getSelectionType(
  tableNode: TableNode,
  selectedCellKeys: Set<string>
): {
  isFullRowSelection: boolean;
  isFullColumnSelection: boolean;
  selectedRowIndices: Set<number>;
  selectedColumnIndices: Set<number>;
  numRows: number;
  numColumns: number;
} {
  const [tableMap] = $computeTableMapSkipCellCheck(tableNode, null, null);
  const numRows = tableMap.length;
  const numColumns = tableMap[0]?.length || 0;

  const selectedRowIndices = new Set<number>();
  const selectedColumnIndices = new Set<number>();

  for (let r = 0; r < numRows; r++) {
    for (let c = 0; c < numColumns; c++) {
      const cellInfo = tableMap[r]?.[c];
      if (cellInfo?.cell && selectedCellKeys.has(cellInfo.cell.getKey())) {
        selectedRowIndices.add(r);
        selectedColumnIndices.add(c);
      }
    }
  }

  const isFullRowSelection =
    selectedColumnIndices.size === numColumns && selectedRowIndices.size > 0;
  const isFullColumnSelection =
    selectedRowIndices.size === numRows && selectedColumnIndices.size > 0;

  return {
    isFullRowSelection,
    isFullColumnSelection,
    selectedRowIndices,
    selectedColumnIndices,
    numRows,
    numColumns,
  };
}
